/* eslint-disable @typescript-eslint/unbound-method */
import { ReactNode, useContext, useEffect, useState } from 'react';

import { JwtCliengoPayload } from './types';
import { UserContext } from './user-context-provider';
import { validateJwtAndAuthorize } from './validate-jwt-and-authorize';

import DefaultProgress from '@/components/default-progress';
import { UnauthorizedPage } from '@/components/unauthorized-page';
import { initLaunchDarkly } from '@/clients/launch-darkly-client';
import { HsmWebsite } from '@/services/hsm-backend/types';
import { useAppDispatch } from '@/hooks/use-redux';
import { initializeCommon } from '@/store/slices/common';
/**
 * `AuthenticationGuard` is a component that verifies user authentication and admin privileges.
 * It displays a loading state during verification, an unauthorized page for non-admin users,
 * and renders children for authorized admin users.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {ReactNode} props.children - Child elements to render when authorized
 * @returns {JSX.Element} Loading state, unauthorized page, or children based on auth status
 *
 *  @example
 *  <AuthenticationGuard>
 *    <ProtectedComponent />
 *  </AuthenticationGuard>
 */
export const AuthenticationGuard = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { setJwtCliengoPayload, setWebsites, setIsAdmin, isAdmin } =
    useContext(UserContext);

  const dispatch = useAppDispatch();

  /**
   * Callback function that handles the authentication verification process.
   * This function is called when the JWT token is successfully validated and contains:
   * - Setting the JWT payload in the user context
   * - Setting the user's websites in the user context
   * - Setting the admin status based on user privileges
   * - Initializing the common store with company and channel information
   *
   * @param {JwtCliengoPayload} jcp - The parsed JWT payload containing user information
   * @param {Website[]} ws - Array of websites associated with the user
   *
   * @example
   * onAuthenticationVerified(jwtPayload, userWebsites);
   */
  const onAuthenticationVerified = (
    jcp: JwtCliengoPayload,
    ws: HsmWebsite[]
  ) => {
    const isUserAdmin =
      jcp.privileges.includes('admin') || jcp.privileges.includes('superadmin');

    setJwtCliengoPayload(jcp);
    setWebsites(ws);

    setIsAdmin(isUserAdmin);
    setIsLoading(false);

    dispatch(
      initializeCommon({
        company: jcp.company,
        channel: ws.length ? ws[0].id : '',
        channelsLoaded: true,
      })
    );

    // Initialize LaunchDarkly with the JWT identity so feature flags (e.g.
    // `feature-segments`) evaluate per-company. Fire-and-forget: it must not
    // block rendering, and flags are consumed later, deep in the wizard.
    void initLaunchDarkly({ userId: jcp.user, companyId: jcp.company });
  };

  useEffect(() => {
    const init = async (): Promise<void> => {
      await validateJwtAndAuthorize(onAuthenticationVerified);
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <DefaultProgress />;
  }

  if (!isAdmin) {
    return <UnauthorizedPage />;
  }

  return <>{children}</>;
};
